import React, { useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Table, Button } from "reactstrap";
import {
  Loading, WarningAlert, PaginationControls, ItemsPerPageSelector, SortableHeaderCell, EntityRow, ConfirmationModal,
  NotificationToast, SimpleFormModal, SearchBar, InformationalModal
} from "../components";
import useSortableData from "../hooks/useSortableData";
import { useEntity } from "../hooks/useEntity";
import { marketContextsApi } from "../api/entitiesApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { marketContextFields, marketContextDefaultValues } from "../utils/entityFormFields";
import { Link } from "react-router-dom";

// styles
import "../App.css";

export function MarketContextsComponent() {
  const { data: marketContexts, isLoading, error, setData: setMarketContexts, setError } = useEntity(marketContextsApi);
  const { items: sortedMarketContexts, requestSort, sortConfig } = useSortableData(marketContexts, { key: '', direction: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [marketContextsPerPage, setMarketContextsPerPage] = useState(50);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentForm, setCurrentForm] = useState(null);
  const [deleteData, setDeleteData] = useState({ open: false, marketContextId: null });
  const [notification, setNotification] = useState({ visible: false, title: "", message: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [errorMessage, setErrorMessage] = useState({ open: false, title: "", message: "" });


  const totalPages = Math.ceil(marketContexts.length / marketContextsPerPage);
  const indexOfLastMarketContext = currentPage * marketContextsPerPage;
  const indexOfFirstMarketContext = indexOfLastMarketContext - marketContextsPerPage;
  const itemsPerPage = [50, 100];
  const filteredMarketContexts = sortedMarketContexts.filter(marketContext => {
    if (!searchTerm) return true;
    const lowerTerm = searchTerm.toLowerCase();
    return (
      (marketContext.id && marketContext.id.toString().toLowerCase().includes(lowerTerm)) ||
      (marketContext.customer_id && marketContext.customer_id.toString().toLowerCase().includes(lowerTerm)) ||
      (marketContext.site_id && marketContext.site_id.toString().toLowerCase().includes(lowerTerm))
    );
  });
  const currentMarketContexts = filteredMarketContexts.slice(indexOfFirstMarketContext, indexOfLastMarketContext);
  function toggleDropdown() { setDropdownOpen(prevState => !prevState); }
  function handlePageChange(pageNumber) { setCurrentPage(pageNumber); }
  function handleDropdownChange(number) {
    setMarketContextsPerPage(number);
    setCurrentPage(1);
  };

  function handleEdit(marketContext) {
    setCurrentForm({ mode: "edit", data: marketContext });
  };

  function handleAdd() {
    setCurrentForm({ mode: "add", data: { customer_id: "", site_id: "" } });
  }

  async function handleSaveForm(formData) {
    try {
      if (currentForm.mode === "edit") {
        await marketContextsApi.updateEntity(formData.id, formData);
        setMarketContexts(prevData =>
          prevData.map(marketContext =>
            marketContext.id === formData.id ? formData : marketContext
          )
        );
        setNotification({ visible: true, title: "MarketContext Edited", message: "1 MarketContext has been edited" });
        setTimeout(() => {
          setNotification({ visible: false, title: "", message: "" });
        }, 2000);
      } else {
        const newMarketContext = await marketContextsApi.addEntity(formData);
        setMarketContexts(prevData => [...prevData, newMarketContext]);
        setNotification({ visible: true, title: "MarketContext Added", message: "1 MarketContext has been added" });
        setTimeout(() => {
          setNotification({ visible: false, title: "", message: "" });
        }, 2000);
      }
    } catch (err) {
      setErrorMessage({ open: true, title: currentForm.mode === "edit" ? "Error Updating MarketContext" : "Error Adding MarketContext", message: err.message });
    } finally {
      setCurrentForm(null);
    }
  }

  function handleCancelFormModal() {
    setCurrentForm(null);
  }

  function openDeleteModal(marketContext) {
    setDeleteData({ open: true, marketContextId: marketContext.id });
  }

  async function confirmDelete() {
    try {
      await marketContextsApi.deleteEntity(deleteData.marketContextId);
      setMarketContexts((prevMarketContexts) =>
        prevMarketContexts.filter((marketContext) => marketContext.id !== deleteData.marketContextId)
      );
      setNotification({ visible: true, title: "MarketContext Deleted", message: "1 MarketContext has been deleted" });
      setTimeout(() => {
        setNotification({ visible: false, title: "", message: "" });
      }, 2000);
    } catch (err) {
      setErrorMessage({ open: true, title: "Error Deleting MarketContext", message: err.message })
    } finally {
      setDeleteData({ open: false, marketContextId: null });
    }
  }

  function cancelDelete() {
    setDeleteData({ open: false, marketContextId: null });
  };

  function dismissInformationalModal() {
    setErrorMessage({ open: false, title: "", message: "" })
  }

  if (isLoading) {
    return <Loading />;
  }

  const displayError = error
    ? error.split("\n").map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ))
    : "Ooops! No marketContexts found";

  return (
    <>
      {marketContexts.length === 0 && (
        <div className="d-flex flex-column align-items-center">
          <WarningAlert message={displayError} />
          <br />
          <div className="d-flex justify-content-center">
            <Button
              color="primary"
              onClick={(e) => {
                e.currentTarget.blur();
                setError(null);
                handleAdd();
              }}
              aria-label={error ? "Create First MarketContext" : "Create MarketContext"}
            >
              Create
            </Button>
          </div>
        </div>
      )}

      {marketContexts.length > 0 && (
        <div className="mb-5">
          <div className="d-flex justify-content-end align-items-center mb-2">
            <div style={{ marginRight: "1rem" }}>
              <Button
                color="transparent"
                onClick={(e) => {
                  e.currentTarget.blur();
                  handleAdd();
                }}
                aria-label="Add MarketContext"
              >
                <FontAwesomeIcon icon="plus" />
              </Button>
            </div>
            <div>
              <SearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />
            </div>
          </div>

          {filteredMarketContexts.length > 0 ? (
            <>
              <Table bordered hover responsive size="sm" striped>
                <thead>
                  <tr>
                    <SortableHeaderCell columnKey={"customer_id"} label={"Customer_id"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"id"} label={"Id"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"site_id"} label={"Site_id"} currentSort={sortConfig} onSort={requestSort} />
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentMarketContexts.map((marketContext) => (
                    <EntityRow
                      key={marketContext.id}
                      entity={marketContext}
                      fields={["customer_id", "id", "site_id"]}
                      onEdit={handleEdit}
                      onDelete={openDeleteModal}
                      isSelected={
                        (currentForm && currentForm.mode === "edit" && marketContext.id === currentForm.data.id) ||
                        marketContext.id === deleteData.marketContextId
                      }
                      renderField={(field, value) => {
                        if (field === "customer_id") {
                          return <Link to={`/customerDetails/${value}`}>{value.substring(0, 4)}</Link>;
                        }
                        return value;
                      }}
                    />
                  ))}
                </tbody>
              </Table>
              <div className="d-flex justify-content-between align-items-center">
                <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                <ItemsPerPageSelector
                  items={itemsPerPage}
                  currentSelection={marketContextsPerPage}
                  onSelect={handleDropdownChange}
                  isOpen={dropdownOpen}
                  toggle={toggleDropdown}
                />
              </div>
            </>
          ) : (
            <WarningAlert message="Ooops! No marketContexts found" />
          )}
        </div>
      )}

      <InformationalModal
        isOpen={errorMessage.open} title={errorMessage.title} message={errorMessage.message}
        onCancel={dismissInformationalModal}
      />

      <ConfirmationModal
        isOpen={deleteData.open} title="Delete MarketContext?" message="Are you sure you want to delete this marketContext?"
        onConfirm={confirmDelete} onCancel={cancelDelete} confirmLabel="OK" cancelLabel="Cancel"
      />

      <SimpleFormModal
        isOpen={!!currentForm} title={currentForm ? (currentForm.mode === "edit" ? "Edit MarketContext" : "Add MarketContext") : ""}
        initialData={currentForm ? currentForm.data : null} fields={marketContextFields()} onSave={handleSaveForm} onCancel={handleCancelFormModal}
        defaultValues={marketContextDefaultValues}
      />

      {notification.visible && (
        <NotificationToast icon="exclamation-triangle" title={notification.title} message={notification.message} fade={true} />
      )}
    </>
  );
};

export default withAuthenticationRequired(MarketContextsComponent, {
  onRedirecting: () => <Loading />,
});
